import type { JsonObject } from '@inf-monkeys-tech/monkeys';
import { compileTargetRenderModelValidator } from '@inf-monkeys-tech/monkeys/runtime';
import { I18N_DISPLAY_TEXT_SCHEMA, LOCALIZED_DISPLAY_PARAMETERS_SCHEMA } from './localizedDisplayValue';
const localizedLabelProperties = { label: { type: 'string' }, labelKey: { type: 'string', minLength: 1 } };
const RECORD_EDITOR_OPTION_GROUP_SCHEMA: JsonObject = {
  type: 'object', additionalProperties: false, required: ['id'],
  anyOf: [{ required: ['label'] }, { required: ['labelKey'] }, { required: ['labelI18n'] }],
  properties: {
    id: { type: 'string', minLength: 1 },
    ...localizedLabelProperties,
    label: { type: 'string', minLength: 1 },
    labelI18n: I18N_DISPLAY_TEXT_SCHEMA,
    labelParameters: LOCALIZED_DISPLAY_PARAMETERS_SCHEMA,
  },
};
export const RECORD_EDITOR_OPTION_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: false,
  required: ["id"],
  properties: {
    id: { type: "string", minLength: 1 },
    ...localizedLabelProperties,
    labelI18n: I18N_DISPLAY_TEXT_SCHEMA,
    labelParameters: LOCALIZED_DISPLAY_PARAMETERS_SCHEMA,
    group: RECORD_EDITOR_OPTION_GROUP_SCHEMA,
    color: { type: "string", minLength: 1 },
    mediaType: { type: "string", minLength: 1 },
    ontologyId: { type: "string", minLength: 1 },
    disabled: { type: "boolean" },
    disabledWhen: { type: "object" },
  },
};

export const RECORD_EDITOR_STRING_OPTION_SCHEMA: JsonObject = { ...RECORD_EDITOR_OPTION_SCHEMA, properties: { ...RECORD_EDITOR_OPTION_SCHEMA.properties as JsonObject, id: { type: 'string' } } };
const optionPathSchema: JsonObject = { type: 'array', minItems: 1, maxItems: 16, items: { type: 'string', minLength: 1, maxLength: 256, pattern: '^[^\\u0000-\\u001f\\u007f-\\u009f]+$', not: { enum: ['__proto__', 'prototype', 'constructor'] } } };
export const RECORD_EDITOR_OPTIONS_SOURCE_SCHEMA: JsonObject = { type: 'object', additionalProperties: false, required: ['modelPath', 'parentKeyPath'], properties: { modelPath: { ...optionPathSchema, minItems: 2, maxItems: 2, prefixItems: [{ const: 'optionTables' }] }, parentKeyPath: optionPathSchema } };
const validateOptions = compileTargetRenderModelValidator({ type: 'object', additionalProperties: false, required: ['options'], properties: { options: { type: 'array', maxItems: 10000, items: RECORD_EDITOR_STRING_OPTION_SCHEMA } } });
/** Dependent candidates share the ordinary string-option schema and local collection budget. */
export function validateRecordEditorOptions(input: unknown): JsonObject[] {
  if (!Array.isArray(input) || input.length > 10000) throw new TypeError('Editor options exceed the candidate limit.');
  const json = JSON.stringify(input);
  if (new TextEncoder().encode(json).byteLength > 1024 * 1024) throw new TypeError('Editor options exceed the byte limit.');
  const result = validateOptions({ options: input }).options;
  if (!Array.isArray(result)) throw new TypeError('Editor options must be an array.');
  return result as JsonObject[];
}

const tableKeySchema: JsonObject = { type: 'string', minLength: 1, maxLength: 256, pattern: '^[^\\u0000-\\u001f\\u007f-\\u009f]+$', not: { enum: ['__proto__', 'prototype', 'constructor'] } };
export const RECORD_EDITOR_OPTION_TABLES_SCHEMA: JsonObject = { type: 'object', propertyNames: tableKeySchema, additionalProperties: { type: 'object', propertyNames: tableKeySchema, additionalProperties: { type: 'array', maxItems: 10000, items: RECORD_EDITOR_STRING_OPTION_SCHEMA } } };
const validateTables = compileTargetRenderModelValidator({ type: 'object', additionalProperties: false, required: ['tables'], properties: { tables: RECORD_EDITOR_OPTION_TABLES_SCHEMA } });
export function validateRecordEditorOptionTables(input: unknown): void {
  if (new TextEncoder().encode(JSON.stringify(input)).byteLength > 1024 * 1024) throw new TypeError('Editor option tables exceed the byte limit.');
  const tables = validateTables({ tables: input }).tables;
  if (!tables || typeof tables !== 'object' || Array.isArray(tables)) throw new TypeError('Invalid editor option tables.');
  let count = 0;
  for (const table of Object.values(tables)) {
    if (!table || typeof table !== 'object' || Array.isArray(table)) throw new TypeError('Invalid editor option table.');
    for (const options of Object.values(table)) {
      if (!Array.isArray(options)) throw new TypeError('Invalid editor option list.');
      count += options.length;
      if (count > 10000) throw new TypeError('Editor option tables exceed the candidate limit.');
    }
  }
}
